import BaseService from '../../BaseService'

import type { CustomAxiosResponse, PageParams } from '../../types'
import type {
  Game,
  GameClassification,
  GameCollection,
  GetGameListOptions,
  PKGameRecord,
  PKGameRecordQuery,
  PKGameRecordSummary,
  Tag
} from '../../types/game'

class GameService extends BaseService {
  /**
   * 获取游戏列表
   * @param options 参数
   * @param options.name 游戏名称
   * @param options.tagCode 游戏标签code
   * @param options.vendorId 游戏厂商id
   * @param options.pageSize 分页大小
   * @param options.pageNum 分页页码
   * @param options.orderBy 排序字段
   * @returns 游戏列表
   */
  public async getGameList(options: GetGameListOptions & PageParams = {}): Promise<CustomAxiosResponse<Game[]>> {
    return this.request.get<unknown, CustomAxiosResponse<Game[]>>(`${this.apiUrl}/v1/game/app/game/`, {
      params: {
        ...options
      }
    })
  }

  /**
   * 获取游戏聚合列表
   * @returns 游戏聚合列表
   */
  public async getGameCollection(): Promise<CustomAxiosResponse<GameCollection>> {
    return this.request.get<unknown, CustomAxiosResponse<GameCollection>>(`${this.apiUrl}/v1/game/app/game/genre`)
  }

  /**
   * 获取游戏标签分类列表
   * @returns 游戏标签分类列表
   */
  public async getGameClassification(): Promise<CustomAxiosResponse<GameClassification[]>> {
    return this.request.get<unknown, CustomAxiosResponse<GameClassification[]>>(
      `${this.apiUrl}/v1/game/app/game/classification`
    )
  }

  /**
   * 获取游戏详情
   * @param code 游戏编码
   * @returns 游戏详情
   */
  public async getGame(code: string): Promise<CustomAxiosResponse<Game>> {
    return this.request.get<unknown, CustomAxiosResponse<Game>>(`${this.apiUrl}/v1/game/app/game/${code}`)
  }

  /**
   * 获取游戏标签列表
   * @param options 参数
   * @param options.pageSize 分页大小
   * @param options.pageNum 分页页码
   * @returns 游戏标签列表
   */
  public async getTagList(options: PageParams = {}): Promise<CustomAxiosResponse<Tag[]>> {
    return this.request.get<unknown, CustomAxiosResponse<Tag[]>>(`${this.apiUrl}/v1/game/app/tag`, {
      params: {
        ...options
      }
    })
  }

  /**
   * 获取游戏标签详情
   * @param code 游戏标签code
   * @returns 游戏标签详情
   */
  public async getTag(code: string): Promise<CustomAxiosResponse<Tag>> {
    return this.request.get<unknown, CustomAxiosResponse<Tag>>(`${this.apiUrl}/v1/game/app/tag/${code}`)
  }

  /**
   * 搜索游戏列表
   * @param keyword 搜索关键字
   * @returns 游戏列表
   */
  public async searchGameList(keyword: string): Promise<CustomAxiosResponse<Game[]>> {
    return this.request.get<unknown, CustomAxiosResponse<Game[]>>(`${this.apiUrl}/v1/game/app/game/search`, {
      params: {
        searchValue: keyword
      }
    })
  }

  /**
   * 智能推荐游戏 根据用户
   * @param options 参数
   * @param options.userId 用户id
   * @returns 游戏列表
   */
  public async getRecommendGamesByUser(options: { userId: string }): Promise<CustomAxiosResponse<Game[]>> {
    return this.request.get<unknown, CustomAxiosResponse<Game[]>>(`${this.apiUrl}/v1/game/app/game/recommend/user/`, {
      params: { ...options }
    })
  }

  /**
   * 智能推荐游戏 根据游戏
   * @param options 参数
   * @param options.code 游戏编码
   * @param options.userId 用户id
   * @returns 游戏列表
   */
  public async getRecommendGamesByGame(options: {
    code: string
    userId: string
  }): Promise<CustomAxiosResponse<Game[]>> {
    return this.request.get<unknown, CustomAxiosResponse<Game[]>>(`${this.apiUrl}/v1/game/app/game/recommend/`, {
      params: { ...options }
    })
  }

  /**
   * 获取对战记录列表
   * @param options 参数
   * @returns 对战记录列表
   */
  public async getPKGameRecordList(
    options: PKGameRecordQuery & PageParams = {}
  ): Promise<CustomAxiosResponse<PKGameRecord[]>> {
    return this.request.get<unknown, CustomAxiosResponse<PKGameRecord[]>>(`${this.apiUrl}/v1/game/app/game/history/`, {
      params: { ...options }
    })
  }

  /**
   * 获取对战记录汇总
   * @param options 参数
   * @returns 对战记录汇总
   */
  public async getPKGameSumary(options: PKGameRecordQuery): Promise<CustomAxiosResponse<PKGameRecordSummary>> {
    return this.request.get<unknown, CustomAxiosResponse<PKGameRecordSummary>>(
      `${this.apiUrl}/v1/game/app/game/history/summary/`,
      {
        params: { ...options }
      }
    )
  }
}

export default GameService
